import { IconProvider, useTimeOfDay } from "@healthos/ui";
import type { ReactNode } from "react";
import { BottomNav } from "./bottom-nav";
import type { DefaultNavItem } from "./nav-items";
import { PageUtilitiesProvider } from "./page-utilities-context";
import { SideNav } from "./side-nav";

export type NavItem = DefaultNavItem;

export interface AppShellProps {
  children: ReactNode;
  navItems?: NavItem[];
  brand?: {
    name: string;
    /** Small eyebrow above the name (e.g. "HealthOS"). */
    eyebrow?: string;
    logoSrc?: string;
    /** Marks the shell as running an opt-in mode. */
    mode?: { label: string; hint: string } | undefined;
  };
  /** Hide the chrome (e.g. for full-bleed reset / embed overlays). */
  hideChrome?: boolean;
  /** Controls the horizontal max-width of the main content column. */
  contentWidth?: "narrow" | "wide" | "full";
  /** Tools rendered within every standard PageLayout. */
  pageUtilities?: ReactNode;
  /** Optional persistent content docked above the desktop sidebar footer. */
  sidebarFooter?: ReactNode;
  /** Opens the member's manual signal logger from the desktop sidebar. */
  onLogClick?: () => void;
}

/**
 * Main content column widths.
 *
 * `wide` is intentionally unconstrained at the shell level, the Today /
 * Plan / Reflection surfaces each own their own responsive container
 * (typically `max-w-[1760px]` with generous `px-*` padding) and their own
 * responsive grids. This way the shell doesn't silently cap a 1920px
 * monitor to 1480px with a dead strip on either side.
 *
 * `narrow` stays constrained for auth / onboarding flows that want a
 * single readable column.
 */
const WIDTHS = {
  narrow: "max-w-md md:max-w-2xl mx-auto",
  wide: "max-w-none",
  full: "max-w-none",
} as const;

export function AppShell({
  children,
  navItems,
  brand,
  hideChrome,
  contentWidth = "wide",
  pageUtilities,
  sidebarFooter,
  onLogClick,
}: AppShellProps) {
  /*
   * `data-time-of-day` cascades the ambient-blob colour custom
   * properties (defined in tokens.css) so the page canvas tint
   * shifts subtly across morning / afternoon / evening / night.
   * The 4s `transition: background` on the mesh blobs (declared
   * in base.css) cross-fades the palette dissolve at boundaries.
   */
  const timeOfDay = useTimeOfDay();
  return (
    <IconProvider>
      <PageUtilitiesProvider utilities={hideChrome ? undefined : pageUtilities}>
        <div data-time-of-day={timeOfDay} className="min-h-screen bg-ambient-mesh">
          {!hideChrome && (
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-navy focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink-on-brand focus:shadow-modal focus:outline-2 focus:outline-offset-2 focus:outline-teal-400"
            >
              Skip to content
            </a>
          )}
          <div className="md:flex md:min-h-screen">
            {!hideChrome && (
              <SideNav
                items={navItems}
                brand={brand}
                footerSlot={sidebarFooter}
                onLogClick={onLogClick}
              />
            )}
            <main
              id="main-content"
              tabIndex={-1}
              className="flex-1 pb-28 md:pb-0 min-h-screen relative flex flex-col focus:outline-none"
            >
              <div className={`w-full flex-1 ${WIDTHS[contentWidth]}`}>{children}</div>
            </main>
          </div>
          {!hideChrome && <BottomNav items={navItems} />}
        </div>
      </PageUtilitiesProvider>
    </IconProvider>
  );
}
